import { axiosInstance } from ".";

const ITEMS_PER_PAGE = 10;

const getCompanyById = async (id, config = {}) =>
  await axiosInstance.get(`/companies/${id}`, config);

/**
 * Shared admin companies index — GET /companies.
 * Registration: { status: "pending", page, name? };
 * Companies list: status, name, dates, page, etc.
 */
const getCompanies = async (params = {}) =>
  await axiosInstance.get("/companies", {
    params: {
      order_by_created_at: "desc",
      page: 1,
      ...params,
      items: params.items ?? ITEMS_PER_PAGE,
    },
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

const patchCompanyAdmin = async (id, data) =>
  await axiosInstance.patch(`/companies/${id}`, data);

/** Approves a registration and assigns its subscription plan, in one request (PROLINE-4136). */
const approveCompany = async (id, subscriptionPlanId, config) =>
  await axiosInstance.patch(
    `/companies/${id}`,
    { status: "approved", subscription_plan_id: subscriptionPlanId },
    config,
  );

/** Moves an approved company to a higher subscription plan, right away (PROLINE-4150). */
const changeCompanySubscriptionPlan = async (id, subscriptionPlanId, config) =>
  await axiosInstance.patch(
    `/companies/${id}`,
    { subscription_plan_id: subscriptionPlanId },
    config,
  );

/** Deactivates an approved company, kept apart from the edit form's fields (PROLINE-4124). */
const deactivateCompany = async (id, reason, config) =>
  await axiosInstance.patch(
    `/companies/${id}`,
    { status: "deactivated", deactivation_reason: reason },
    config,
  );

/** Brings a deactivated company back: the status only, no other field (PROLINE-4213). */
const activateCompany = async (id, config) =>
  await axiosInstance.patch(`/companies/${id}`, { status: "approved" }, config);

const rejectCompany = async (id, rejectionReason) =>
  await axiosInstance.delete(`/companies/${id}/reject`, {
    data: { rejection_reason: rejectionReason },
  });

/** Admin company edit requests — PROLINE-2212 */
const getCompanyEditRequestById = async (id, config = {}) =>
  await axiosInstance.get(`/company_edit_requests/${id}`, config);

const patchCompanyEditRequest = async (id, data) =>
  await axiosInstance.patch(`/company_edit_requests/${id}`, data);

/**
 * Profile Edit Requests list — GET /company_edit_requests (PROLINE-3854).
 * `status` is comma-separated (pending, in_review, approved, rejected).
 */
const getCompanyEditRequests = async (params = {}) =>
  await axiosInstance.get("/company_edit_requests", {
    params: {
      order_by_created_at: "desc",
      page: 1,
      ...params,
      items: params.items ?? ITEMS_PER_PAGE,
    },
    headers: {
      "Cache-Control": "no-cache",
      Pragma: "no-cache",
    },
  });

/** Normalize pagination headers from GET /companies */
const metaFromCompaniesResponse = (response) => ({
  currentPage: +response.headers["current-page"],
  count: +response.headers["total-count"],
  pages: +response.headers["total-pages"],
});

export {
  getCompanyById,
  getCompanies,
  metaFromCompaniesResponse,
  patchCompanyAdmin,
  approveCompany,
  changeCompanySubscriptionPlan,
  deactivateCompany,
  activateCompany,
  rejectCompany,
  getCompanyEditRequestById,
  patchCompanyEditRequest,
  getCompanyEditRequests,
  ITEMS_PER_PAGE,
};
